import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/design05/Header';
import {
    Coffee,
    Globe,
    Award,
    Shield,
    Star,
    MapPin,
    Mail,
    Phone,
    Users,
    CheckCircle,
    ArrowRight,
    Leaf,
    Zap,
    TrendingUp,
    Package,
    ThumbsUp,
} from 'lucide-react';

const HomePage = () => {
    useEffect(() => {
        const setNamedMeta = (name, content) => {
            let tag = document.querySelector(`meta[name="${name}"]`);
            if (!tag) {
                tag = document.createElement('meta');
                tag.setAttribute('name', name);
                document.head.appendChild(tag);
            }
            tag.setAttribute('content', content);
        };

        const setPropertyMeta = (property, content) => {
            let tag = document.querySelector(`meta[property="${property}"]`);
            if (!tag) {
                tag = document.createElement('meta');
                tag.setAttribute('property', property);
                document.head.appendChild(tag);
            }
            tag.setAttribute('content', content);
        };

        const setCanonical = (href) => {
            let link = document.querySelector('link[rel="canonical"]');
            if (!link) {
                link = document.createElement('link');
                link.setAttribute('rel', 'canonical');
                document.head.appendChild(link);
            }
            link.setAttribute('href', href);
        };

        document.title =
            "Claude's Special - Premium Ceylon Cinnamon Exports | Sri Lankan Spices";
        setNamedMeta(
            'description',
            "Claude's Special exports the finest Ceylon Cinnamon from Sri Lanka to Australia. Discover authentic, premium quality cinnamon with rich heritage and unmatched flavor."
        );
        setNamedMeta(
            'keywords',
            'Ceylon Cinnamon, Sri Lankan spices, cinnamon export, authentic cinnamon, premium spices, Australia import'
        );
        setPropertyMeta(
            'og:title',
            "Claude's Special - Premium Ceylon Cinnamon Exports"
        );
        setPropertyMeta(
            'og:description',
            'Exporting the finest Ceylon Cinnamon from Sri Lanka to Australia with authentic quality and rich heritage.'
        );
        setPropertyMeta('og:type', 'website');
        setCanonical('/');
    }, []);

    return (
        <>
            <Header />

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-black">
                {/* Geometric Background */}
                <div className="absolute inset-0">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-orange-500/20 via-red-500/10 to-yellow-500/20"></div>
                    <div className="absolute top-20 left-20 w-96 h-96 bg-gradient-to-br from-orange-500/30 to-red-500/30 transform rotate-45 blur-3xl"></div>
                    <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-br from-yellow-500/30 to-orange-500/30 transform -rotate-45 blur-3xl"></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-br from-red-500/20 to-pink-500/20 rounded-full blur-2xl"></div>
                </div>

                <div className="relative z-10 text-center max-w-7xl mx-auto px-6 pt-32">
                    {/* Geometric Badge */}
                    <div className="inline-flex items-center justify-center mb-12">
                        <div className="relative">
                            <div className="w-32 h-32 bg-gradient-to-br from-orange-500 to-red-600 transform rotate-45 flex items-center justify-center shadow-2xl">
                                <Coffee className="h-16 w-16 text-white transform -rotate-45" />
                            </div>
                            <div className="absolute -top-4 -right-4 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center">
                                <Star className="h-6 w-6 text-gray-900" />
                            </div>
                        </div>
                    </div>

                    <h1 className="text-8xl md:text-9xl font-black mb-8 leading-none tracking-tighter">
                        <span className="block text-white mb-4">CLAUDE'S</span>
                        <span className="block bg-gradient-to-r from-orange-400 via-red-500 to-yellow-400 bg-clip-text text-transparent">
                            SPECIAL
                        </span>
                    </h1>

                    <div className="w-32 h-2 bg-gradient-to-r from-orange-500 to-red-600 mx-auto mb-12"></div>

                    <p className="text-3xl md:text-4xl mb-8 font-bold text-gray-300 tracking-wide">
                        PREMIUM CEYLON CINNAMON
                    </p>

                    <p className="text-xl mb-16 text-gray-400 max-w-4xl mx-auto leading-relaxed">
                        Exporting Sri Lanka&apos;s finest Ceylon Cinnamon to Australia with
                        uncompromising quality, authentic heritage, and modern excellence.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-8 justify-center items-center mb-20">
                        <Link
                            to="/ceylon-cinnamon"
                            className="group bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-12 py-6 text-xl font-black tracking-wide transition-all duration-500 transform hover:scale-110 shadow-2xl hover:shadow-orange-500/50 flex items-center space-x-4"
                        >
                            <span>DISCOVER</span>
                            <ArrowRight className="h-6 w-6 group-hover:translate-x-2 transition-transform duration-300" />
                        </Link>
                        <a
                            href="#about"
                            className="bg-transparent border-4 border-white hover:bg-white hover:text-gray-900 text-white px-12 py-6 text-xl font-black tracking-wide transition-all duration-500 transform hover:scale-110"
                        >
                            LEARN MORE
                        </a>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-16 pb-20">
                        <div className="text-center group">
                            <div className="w-24 h-24 bg-gradient-to-br from-orange-500 to-red-600 transform rotate-45 flex items-center justify-center mx-auto mb-6 group-hover:rotate-90 transition-transform duration-500 shadow-xl">
                                <div className="text-3xl font-black text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500">
                                    25+
                                </div>
                            </div>
                            <div className="text-lg text-gray-300 font-bold tracking-wide">
                                YEARS
                            </div>
                        </div>
                        <div className="text-center group">
                            <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 transform rotate-45 flex items-center justify-center mx-auto mb-6 group-hover:rotate-90 transition-transform duration-500 shadow-xl">
                                <div className="text-2xl font-black text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500">
                                    100%
                                </div>
                            </div>
                            <div className="text-lg text-gray-300 font-bold tracking-wide">
                                AUTHENTIC
                            </div>
                        </div>
                        <div className="text-center group">
                            <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 transform rotate-45 flex items-center justify-center mx-auto mb-6 group-hover:rotate-90 transition-transform duration-500 shadow-xl">
                                <div className="text-3xl font-black text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500">
                                    50+
                                </div>
                            </div>
                            <div className="text-lg text-gray-300 font-bold tracking-wide">
                                PARTNERS
                            </div>
                        </div>
                        <div className="text-center group">
                            <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-600 transform rotate-45 flex items-center justify-center mx-auto mb-6 group-hover:rotate-90 transition-transform duration-500 shadow-xl">
                                <div className="text-2xl font-black text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500">
                                    99.9%
                                </div>
                            </div>
                            <div className="text-lg text-gray-300 font-bold tracking-wide">
                                QUALITY
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section id="about" className="py-32 bg-white relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
                    <div className="grid lg:grid-cols-2 gap-20 items-center">
                        <div className="relative">
                            <div className="absolute -top-8 -left-8 w-32 h-32 bg-gradient-to-br from-orange-500 to-red-600 transform rotate-45"></div>
                            <div className="relative bg-gray-900 p-12 shadow-2xl">
                                <img
                                    src="https://images.pexels.com/photos/4198006/pexels-photo-4198006.jpeg"
                                    alt="Traditional cinnamon processing"
                                    className="w-full h-80 object-cover mb-8"
                                />
                                <div className="absolute -bottom-8 -right-8 bg-gradient-to-br from-yellow-400 to-orange-500 p-8 shadow-2xl text-gray-900">
                                    <div className="text-center">
                                        <Coffee className="h-12 w-12 mx-auto mb-4" />
                                        <div className="text-2xl font-black">PREMIUM</div>
                                        <div className="text-lg font-bold">HERITAGE</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <div className="inline-block bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-4 mb-8 font-black tracking-widest">
                                OUR STORY
                            </div>

                            <h2 className="text-6xl font-black text-gray-900 mb-8 leading-tight tracking-tight">
                                CRAFTING
                                <br />
                                <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                                    EXCELLENCE
                                </span>
                            </h2>

                            <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-red-600 mb-8"></div>

                            <p className="text-xl text-gray-600 mb-12 leading-relaxed">
                                Since 1999, Claude&apos;s Special has been the bridge between
                                Sri Lanka&apos;s ancient cinnamon traditions and Australia&apos;s
                                most demanding markets.
                            </p>

                            <div className="space-y-8 mb-12">
                                <div className="flex items-start space-x-6">
                                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 transform rotate-45 flex items-center justify-center shadow-xl">
                                        <Award className="h-8 w-8 text-white transform -rotate-45" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-gray-900 mb-3 tracking-wide">
                                            CERTIFIED QUALITY
                                        </h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            ISO certification with HACCP compliance for international
                                            excellence.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-6">
                                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 transform rotate-45 flex items-center justify-center shadow-xl">
                                        <Users className="h-8 w-8 text-white transform -rotate-45" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-gray-900 mb-3 tracking-wide">
                                            ARTISAN PARTNERS
                                        </h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            Direct relationships with master craftsmen ensuring
                                            sustainability.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-6">
                                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 transform rotate-45 flex items-center justify-center shadow-xl">
                                        <Globe className="h-8 w-8 text-white transform -rotate-45" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-gray-900 mb-3 tracking-wide">
                                            GLOBAL REACH
                                        </h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            Seamless export operations connecting excellence
                                            worldwide.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <Link
                                to="/ceylon-cinnamon"
                                className="inline-flex items-center bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-10 py-5 text-xl font-black tracking-wide transition-all duration-300 transform hover:scale-105 shadow-xl space-x-4"
                            >
                                <span>EXPLORE</span>
                                <ArrowRight className="h-6 w-6" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-32 bg-gray-100">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-20">
                        <div className="inline-block bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-4 mb-8 font-black tracking-widest">
                            WHY CHOOSE US
                        </div>
                        <h2 className="text-6xl font-black text-gray-900 mb-8 tracking-tight">
                            UNMATCHED
                            <br />
                            <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                                EXCELLENCE
                            </span>
                        </h2>
                        <div className="w-32 h-2 bg-gradient-to-r from-orange-500 to-red-600 mx-auto"></div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Feature 1 */}
                        <div className="group bg-white shadow-2xl hover:shadow-orange-500/20 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-500 to-red-600"></div>
                            <div className="p-12">
                                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-600 transform rotate-45 flex items-center justify-center mb-8 group-hover:rotate-90 transition-transform duration-500 shadow-xl">
                                    <Shield className="h-10 w-10 text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-6 tracking-wide">
                                    QUALITY ASSURANCE
                                </h3>
                                <p className="text-gray-600 leading-relaxed mb-8">
                                    Rigorous testing ensures only the finest Ceylon Cinnamon
                                    reaches your customers.
                                </p>
                                <div className="flex items-center text-orange-600 font-bold">
                                    <CheckCircle className="h-5 w-5 mr-3" />
                                    <span>ISO CERTIFIED</span>
                                </div>
                            </div>
                        </div>

                        {/* Feature 2 */}
                        <div className="group bg-white shadow-2xl hover:shadow-blue-500/20 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-500 to-indigo-600"></div>
                            <div className="p-12">
                                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 transform rotate-45 flex items-center justify-center mb-8 group-hover:rotate-90 transition-transform duration-500 shadow-xl">
                                    <Zap className="h-10 w-10 text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-6 tracking-wide">
                                    FAST EXPORT
                                </h3>
                                <p className="text-gray-600 leading-relaxed mb-8">
                                    Efficient logistics ensure quick and safe delivery to
                                    Australia.
                                </p>
                                <div className="flex items-center text-blue-600 font-bold">
                                    <CheckCircle className="h-5 w-5 mr-3" />
                                    <span>5-10 DAYS</span>
                                </div>
                            </div>
                        </div>

                        {/* Feature 3 */}
                        <div className="group bg-white shadow-2xl hover:shadow-green-500/20 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-500 to-emerald-600"></div>
                            <div className="p-12">
                                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 transform rotate-45 flex items-center justify-center mb-8 group-hover:rotate-90 transition-transform duration-500 shadow-xl">
                                    <Leaf className="h-10 w-10 text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-6 tracking-wide">
                                    SUSTAINABLE
                                </h3>
                                <p className="text-gray-600 leading-relaxed mb-8">
                                    Ethical partnerships ensure environmental sustainability.
                                </p>
                                <div className="flex items-center text-green-600 font-bold">
                                    <CheckCircle className="h-5 w-5 mr-3" />
                                    <span>ECO-FRIENDLY</span>
                                </div>
                            </div>
                        </div>

                        {/* Feature 4 */}
                        <div className="group bg-white shadow-2xl hover:shadow-purple-500/20 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-purple-500 to-pink-600"></div>
                            <div className="p-12">
                                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 transform rotate-45 flex items-center justify-center mb-8 group-hover:rotate-90 transition-transform duration-500 shadow-xl">
                                    <Package className="h-10 w-10 text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-6 tracking-wide">
                                    FLEXIBLE PACKAGING
                                </h3>
                                <p className="text-gray-600 leading-relaxed mb-8">
                                    Custom solutions from bulk to retail-ready formats.
                                </p>
                                <div className="flex items-center text-purple-600 font-bold">
                                    <CheckCircle className="h-5 w-5 mr-3" />
                                    <span>CUSTOM SOLUTIONS</span>
                                </div>
                            </div>
                        </div>

                        {/* Feature 5 */}
                        <div className="group bg-white shadow-2xl hover:shadow-yellow-500/20 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-yellow-500 to-orange-500"></div>
                            <div className="p-12">
                                <div className="w-20 h-20 bg-gradient-to-br from-yellow-500 to-orange-500 transform rotate-45 flex items-center justify-center mb-8 group-hover:rotate-90 transition-transform duration-500 shadow-xl">
                                    <ThumbsUp className="h-10 w-10 text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-6 tracking-wide">
                                    EXPERT SUPPORT
                                </h3>
                                <p className="text-gray-600 leading-relaxed mb-8">
                                    Dedicated specialists provide personalized support.
                                </p>
                                <div className="flex items-center text-yellow-600 font-bold">
                                    <CheckCircle className="h-5 w-5 mr-3" />
                                    <span>24/7 SUPPORT</span>
                                </div>
                            </div>
                        </div>

                        {/* Feature 6 */}
                        <div className="group bg-white shadow-2xl hover:shadow-teal-500/20 transition-all duration-500 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-teal-500 to-cyan-600"></div>
                            <div className="p-12">
                                <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-600 transform rotate-45 flex items-center justify-center mb-8 group-hover:rotate-90 transition-transform duration-500 shadow-xl">
                                    <TrendingUp className="h-10 w-10 text-white transform -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
                                </div>
                                <h3 className="text-2xl font-black text-gray-900 mb-6 tracking-wide">
                                    COMPETITIVE PRICING
                                </h3>
                                <p className="text-gray-600 leading-relaxed mb-8">
                                    Premium Ceylon Cinnamon at competitive wholesale prices.
                                </p>
                                <div className="flex items-center text-teal-600 font-bold">
                                    <CheckCircle className="h-5 w-5 mr-3" />
                                    <span>BEST VALUE</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
                <div className="absolute inset-0">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-orange-500/20 via-red-500/10 to-yellow-500/20"></div>
                    <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-orange-500/30 to-red-500/30 transform rotate-45 blur-3xl"></div>
                    <div className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-br from-yellow-500/30 to-orange-500/30 transform -rotate-45 blur-3xl"></div>
                </div>

                <div className="max-w-5xl mx-auto text-center px-6 lg:px-12 relative">
                    <div className="inline-block bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-4 mb-12 font-black tracking-widest">
                        READY TO START?
                    </div>

                    <h2 className="text-6xl md:text-7xl font-black text-white mb-8 leading-tight tracking-tight">
                        BEGIN YOUR
                        <br />
                        <span className="bg-gradient-to-r from-orange-400 via-red-500 to-yellow-400 bg-clip-text text-transparent">
                            JOURNEY
                        </span>
                    </h2>

                    <div className="w-32 h-2 bg-gradient-to-r from-orange-500 to-red-600 mx-auto mb-12"></div>

                    <p className="text-xl text-gray-300 mb-16 leading-relaxed max-w-4xl mx-auto">
                        Join Australia&apos;s most successful importers who trust Claude&apos;s
                        Special for authentic Ceylon Cinnamon.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
                        <a
                            href="#contact"
                            className="group bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-12 py-6 text-xl font-black tracking-wide transition-all duration-500 transform hover:scale-110 shadow-2xl hover:shadow-orange-500/50 flex items-center space-x-4"
                        >
                            <span>GET QUOTE</span>
                            <ArrowRight className="h-6 w-6 group-hover:translate-x-2 transition-transform duration-300" />
                        </a>
                        <Link
                            to="/ceylon-cinnamon"
                            className="bg-transparent border-4 border-white hover:bg-white hover:text-gray-900 text-white px-12 py-6 text-xl font-black tracking-wide transition-all duration-500 transform hover:scale-110"
                        >
                            EXPLORE PRODUCTS
                        </Link>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-32 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-20">
                        <div className="inline-block bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-4 mb-8 font-black tracking-widest">
                            GET IN TOUCH
                        </div>
                        <h2 className="text-6xl font-black text-gray-900 mb-8 tracking-tight">
                            READY TO
                            <br />
                            <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                                IMPORT?
                            </span>
                        </h2>
                        <div className="w-32 h-2 bg-gradient-to-r from-orange-500 to-red-600 mx-auto"></div>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-16">
                        {/* Contact Info */}
                        <div className="space-y-8">
                            <div className="bg-gray-900 p-12 shadow-2xl">
                                <h3 className="text-3xl font-black text-white mb-8 tracking-wide">
                                    EXPORT OFFICE
                                </h3>
                                <div className="space-y-8">
                                    <div className="flex items-start space-x-6">
                                        <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 transform rotate-45 flex items-center justify-center flex-shrink-0 shadow-xl">
                                            <MapPin className="h-8 w-8 text-white transform -rotate-45" />
                                        </div>
                                        <div>
                                            <h4 className="font-black text-white mb-2 text-xl tracking-wide">
                                                LOCATION
                                            </h4>
                                            <p className="text-gray-300">Colombo, Sri Lanka</p>
                                            <p className="text-orange-400 font-bold">
                                                Export Processing Zone
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start space-x-6">
                                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 transform rotate-45 flex items-center justify-center flex-shrink-0 shadow-xl">
                                            <Phone className="h-8 w-8 text-white transform -rotate-45" />
                                        </div>
                                        <div>
                                            <h4 className="font-black text-white mb-2 text-xl tracking-wide">
                                                PHONE
                                            </h4>
                                            <p className="text-gray-300">+94 11 XXX XXXX</p>
                                            <p className="text-blue-400 font-bold">Export Hotline</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start space-x-6">
                                        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 transform rotate-45 flex items-center justify-center flex-shrink-0 shadow-xl">
                                            <Mail className="h-8 w-8 text-white transform -rotate-45" />
                                        </div>
                                        <div>
                                            <h4 className="font-black text-white mb-2 text-xl tracking-wide">
                                                EMAIL
                                            </h4>
                                            <p className="text-gray-300">exports@claudesspecial.lk</p>
                                            <p className="text-green-400 font-bold">24/7 Response</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="bg-gray-100 p-12 shadow-2xl">
                            <h3 className="text-3xl font-black text-gray-900 mb-8 tracking-wide">
                                EXPORT INQUIRY
                            </h3>
                            <form className="space-y-6">
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-black text-gray-700 mb-3 tracking-wide">
                                            COMPANY NAME *
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-6 py-4 border-4 border-gray-300 focus:border-orange-500 focus:outline-none transition-colors duration-200 font-bold"
                                            placeholder="Your company"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-black text-gray-700 mb-3 tracking-wide">
                                            CONTACT PERSON *
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-6 py-4 border-4 border-gray-300 focus:border-orange-500 focus:outline-none transition-colors duration-200 font-bold"
                                            placeholder="Your name"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-black text-gray-700 mb-3 tracking-wide">
                                        EMAIL ADDRESS *
                                    </label>
                                    <input
                                        type="email"
                                        className="w-full px-6 py-4 border-4 border-gray-300 focus:border-orange-500 focus:outline-none transition-colors duration-200 font-bold"
                                        placeholder="your@company.com"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-black text-gray-700 mb-3 tracking-wide">
                                        MESSAGE
                                    </label>
                                    <textarea
                                        rows={6}
                                        className="w-full px-6 py-4 border-4 border-gray-300 focus:border-orange-500 focus:outline-none transition-colors duration-200 resize-none font-bold"
                                        placeholder="Tell us about your import requirements..."
                                    ></textarea>
                                </div>

                                <button
                                    type="submit"
                                    className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white py-6 text-xl font-black tracking-wide transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-orange-500/50 flex items-center justify-center space-x-4"
                                >
                                    <span>SEND INQUIRY</span>
                                    <ArrowRight className="h-6 w-6" />
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-16">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center">
                        <div className="flex items-center justify-center space-x-4 mb-8">
                            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 transform rotate-45 flex items-center justify-center shadow-xl">
                                <Coffee className="h-8 w-8 text-white transform -rotate-45" />
                            </div>
                            <div>
                                <div className="text-3xl font-black text-white tracking-wide">
                                    CLAUDE&apos;S SPECIAL
                                </div>
                                <div className="text-orange-400 font-bold tracking-widest">
                                    CEYLON CINNAMON
                                </div>
                            </div>
                        </div>
                        <div className="w-32 h-1 bg-gradient-to-r from-orange-500 to-red-600 mx-auto mb-8"></div>
                        <p className="text-gray-300 mb-8 max-w-3xl mx-auto text-lg">
                            The world&apos;s finest Ceylon Cinnamon, sourced directly from Sri
                            Lanka&apos;s pristine highlands and delivered to Australia with
                            uncompromising quality.
                        </p>
                        <div className="flex justify-center space-x-12 text-gray-300 mb-8">
                            <span className="flex items-center space-x-3">
                                <Coffee className="h-6 w-6 text-orange-400" />
                                <span className="font-bold">PREMIUM QUALITY</span>
                            </span>
                            <span className="flex items-center space-x-3">
                                <Shield className="h-6 w-6 text-green-400" />
                                <span className="font-bold">CERTIFIED</span>
                            </span>
                            <span className="flex items-center space-x-3">
                                <Globe className="h-6 w-6 text-blue-400" />
                                <span className="font-bold">GLOBAL EXPORT</span>
                            </span>
                        </div>
                        <p className="text-gray-500 font-bold tracking-wide">
                            &copy; 2025 CLAUDE&apos;S SPECIAL. ALL RIGHTS RESERVED.
                        </p>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default HomePage;
